/**
 * Client-side tRPC + TanStack Query setup.
 *
 * Wave 7 W7-1 (this file): replaces the dev-header escape hatch with real
 * better-auth cookie auth. The browser sends the better-auth session cookie
 * automatically (`credentials: "include"` on fetch / `withCredentials: true`
 * on EventSource). The active workspace id is read from the
 * `commerco_active_workspace` cookie set by the WorkspaceSelector chip and
 * forwarded as the `x-commerco-workspace-id` header — the tRPC
 * `createContext` validates it against `workspace_members` for the current
 * user before trusting it, so a tampered value cannot grant cross-tenant
 * access.
 *
 * Wave 7 W7-3 (preserved): subscription procedures (`ask.send.stream`) are
 * routed through `unstable_httpSubscriptionLink as httpSubscriptionLink` (SSE / EventSource) while queries +
 * mutations stay on `httpBatchLink`. The fallback flag
 * `NEXT_PUBLIC_ASK_TRANSPORT=mutation` collapses to a single batch link.
 *
 *   - `trpc` — typed React-Query hooks (`trpc.act.proposals.list.useQuery`)
 *   - `makeTrpcClient` — builds the client with cookie + workspace forwarding
 *   - `getBaseUrl()` — works in browser, Node SSR, and Vercel preview
 */

import { createTRPCReact } from "@trpc/react-query";
import {
  httpBatchLink,
  unstable_httpSubscriptionLink as httpSubscriptionLink,
  splitLink,
} from "@trpc/client";
import superjson from "superjson";
import type { AppRouter } from "@commerco/trpc";

export const trpc = createTRPCReact<AppRouter>();

/** Build-time flag — see `useAsk` for the matching client-side branch. */
export const ASK_TRANSPORT: "subscription" | "mutation" =
  process.env["NEXT_PUBLIC_ASK_TRANSPORT"] === "mutation"
    ? "mutation"
    : "subscription";

const WORKSPACE_COOKIE = "commerco_active_workspace";

function getBaseUrl(): string {
  if (typeof window !== "undefined") return "";
  if (process.env["VERCEL_URL"]) return `https://${process.env["VERCEL_URL"]}`;
  return `http://localhost:${process.env["PORT"] ?? 3000}`;
}

/** Read a same-domain cookie value. Returns null on the server side. */
function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${name}=([^;]*)`),
  );
  return m ? decodeURIComponent(m[1]!) : null;
}

/**
 * Headers to forward on every tRPC request.
 *
 * The session cookie itself is attached by the browser automatically via
 * `credentials: "include"` — we never read/write it from JS (HttpOnly).
 * Only the active workspace id (non-secret) is threaded here.
 */
function authHeaders(): Record<string, string> {
  const workspaceId = readCookie(WORKSPACE_COOKIE);
  return workspaceId ? { "x-commerco-workspace-id": workspaceId } : {};
}

export function makeTrpcClient() {
  const trpcUrl = `${getBaseUrl()}/api/trpc`;

  const batch = httpBatchLink({
    url: trpcUrl,
    transformer: superjson,
    headers: authHeaders,
    fetch(input, init) {
      return fetch(input, {
        ...init,
        credentials: "include",
      });
    },
  });

  // Fallback path: no subscription link wired.
  if (ASK_TRANSPORT === "mutation") {
    return trpc.createClient({
      links: [batch],
    });
  }

  // Default path: split queries/mutations onto httpBatchLink and
  // subscriptions onto httpSubscriptionLink (SSE via EventSource).
  const subscription = httpSubscriptionLink({
    url: trpcUrl,
    transformer: superjson,
    eventSourceOptions() {
      return {
        // Carry the auth cookie on the EventSource handshake. The active
        // workspace id rides via query-string (unstable_httpSubscriptionLink as httpSubscriptionLink handles
        // it internally) because browser EventSource cannot send custom
        // headers — the server still reads the workspace cookie directly
        // and validates membership.
        withCredentials: true,
      };
    },
  });

  return trpc.createClient({
    links: [
      splitLink({
        condition: (op) => op.type === "subscription",
        true: subscription,
        false: batch,
      }),
    ],
  });
}

export type { AppRouter };
